"use client";

import Requires from "internal_components/Requires";
import OfferModel from "./offerModel";
import { useEffect, useState } from "react";
import { Product, Resource } from "@prisma/client";
import { FetchedOffer } from "./actions";
import ShowImageIfPresent from "./ShowImageIfPresent";
import { ChevronUpIcon } from "@heroicons/react/20/solid";
import { AnimatePresence, motion } from "framer-motion";

const OfferRow = ({ fetchedOffer }: { fetchedOffer: FetchedOffer }) => {
  const [showDetails, setShowDetails] = useState<boolean>(false);
  const offerModel = new OfferModel(fetchedOffer);
  const offer = offerModel.offer;
  const [loading, setLoading] = useState<boolean>(true);
  const [products, setProducts] = useState<
    Array<Product & { image?: Resource }>
  >([]);

  useEffect(() => {
    (async () => {
      const offerProducts = offer.package.packagesProducts.map(
        (pp) => pp.product
      );

      setProducts(offerProducts);

      setLoading(false);
    })();
  }, [fetchedOffer.id]);

  if (loading) {
    return <div>Loading...</div>;
  }
  if (offer.name.toLowerCase().includes("shipping")) {
    return null;
  } else {
    return (
      <div key={offer.id} className="mt-2 rounded">
        <div className="flex flex-col justify-between">
          <div className="flex flex-row">
            <ShowImageIfPresent
              image={offer.thumbnail}
              className="w-40 h-40 rounded mx-auto mb-2"
              alt={`${offer.name}  thumbnail`}
            />
          </div>
          <div className="flex flex-row justify-between">
            <div className="font-bold align-left">{offer.name}</div>
            <div className="text-sm">
              <Requires value={offer.msrpInCents}>
                <p className="text-sm line-through justify-end">
                  ${offerModel.getDisplayOfferValue()}
                </p>
              </Requires>
            </div>
          </div>
        </div>

        <div className="flex flex-row justify-between">
          <Requires value={products.length > 1}>
            <div
              onClick={() => setShowDetails(!showDetails)}
              className="flex items-center text-sm cursor-pointer text-blue-400 hover:text-blue-500 underline"
            >
              {showDetails ? "Hide Details" : `Details`}
              <ChevronUpIcon
                className={`h-4 w-4 transition duration-300 ${
                  showDetails ? "" : "transform rotate-180"
                }`}
              />
            </div>
          </Requires>
          <Requires value={products.length === 1}>
            {/* necessary to push price to the right */}
            <div></div>
          </Requires>
          <div className="text-sm">
            <Requires value={offer.numberOfInstallmentsAllowed === 1}>
              <p className="text-red-700 text-right">
                ${offerModel.oneTimePrice()}
              </p>
            </Requires>
            <Requires value={offer.numberOfInstallmentsAllowed > 1}>
              <p className="text-red-700">
                {offer.numberOfInstallmentsAllowed} installments of $
                {offerModel.computeInstallmentPrice()}
              </p>
            </Requires>
          </div>
        </div>

        <AnimatePresence>
          <Requires value={showDetails}>
            {products
              .sort((a, b) => a.name.localeCompare(b.name))
              .map((product) => {
                return (
                  <motion.div
                    key={offer.id + product.id}
                    className="flex"
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    exit={{ opacity: 0, height: 0 }}
                  >
                    <div>
                      <div className="ml-2 text-sm">{product.name}</div>
                      <div className="ml-2 text-xs text-gray-700">
                        {product.description}
                      </div>
                    </div>
                  </motion.div>
                );
              })}
          </Requires>
        </AnimatePresence>
      </div>
    );
  }
};

export default OfferRow;
